import { create } from 'zustand'
import { resolveBindings, DEFAULT_SHORTCUTS, getDefaultKeys } from '../lib/shortcuts'
import { getMeta, setMeta, isDbReady } from '../lib/db'

const META_KEY = 'web:shortcuts'

interface ShortcutsState {
  overrides: Record<string, string>
  resolved: Record<string, string>
  getBinding: (id: string) => string
  setBinding: (id: string, combo: string) => void
  resetBinding: (id: string) => void
  resetAll: () => void
}

function getDefault(id: string): string {
  const def = DEFAULT_SHORTCUTS.find((s) => s.id === id)
  return def ? getDefaultKeys(def) : ''
}

function persist(overrides: Record<string, string>) {
  if (isDbReady()) {
    setMeta(META_KEY, JSON.stringify(overrides))
  }
}

export const useShortcutsStore = create<ShortcutsState>()((set, get) => ({
  overrides: {},
  resolved: resolveBindings({}),

  getBinding: (id: string) => get().resolved[id] ?? getDefault(id),

  setBinding: (id: string, combo: string) => {
    const overrides = { ...get().overrides, [id]: combo }
    const resolved = resolveBindings(overrides)
    persist(overrides)
    set({ overrides, resolved })
  },

  resetBinding: (id: string) => {
    const overrides = { ...get().overrides }
    delete overrides[id]
    const resolved = resolveBindings(overrides)
    persist(overrides)
    set({ overrides, resolved })
  },

  resetAll: () => {
    const overrides = {}
    const resolved = resolveBindings(overrides)
    persist(overrides)
    set({ overrides, resolved })
  },
}))

/** Load persisted overrides from SQLite. Called after DB init. */
export function loadShortcutOverrides(): void {
  try {
    const value = getMeta(META_KEY)
    if (value) {
      const stored = JSON.parse(value) as Record<string, string>
      // Drop overrides for shortcut ids that no longer exist (an action cut in
      // a later release): `resolveBindings` already ignores them, but
      // keeping them would re-persist a dead binding and let it surface as a
      // phantom conflict if the id is ever reused.
      const known = new Set(DEFAULT_SHORTCUTS.map((s) => s.id))
      const overrides = Object.fromEntries(
        Object.entries(stored).filter(([id]) => known.has(id)),
      )
      if (Object.keys(overrides).length !== Object.keys(stored).length) {
        setMeta(META_KEY, JSON.stringify(overrides))
      }
      useShortcutsStore.setState({
        overrides,
        resolved: resolveBindings(overrides),
      })
    }
  } catch {
    // ignore corrupt data
  }
}
